import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Icon,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import {
  FiTruck,
  FiPackage,
  FiRotateCcw,
  FiClock,
  FiCheckCircle,
  FiPhone,
  FiMail,
  FiAlertCircle,
} from 'react-icons/fi';

function ShippingPolicy() {
  return (
    <Box bg="#F8FAFC" minH="100vh" py={{ base: 8, md: 12 }}>
      {/* Header Banner */}
      <Box
        bg="linear-gradient(135deg, #0B132B 0%, #162237 60%, #1E293B 100%)"
        color="white"
        py={{ base: 12, md: 16 }}
        mb={{ base: 8, md: 12 }}
      >
        <Container maxW="5xl" px={{ base: 4, md: 8 }} textAlign="center">
          <Badge
            bg="rgba(234, 179, 8, 0.15)"
            color="#FACC15"
            border="1px solid"
            borderColor="rgba(234, 179, 8, 0.3)"
            px={3.5}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight="semibold"
            letterSpacing="wider"
            textTransform="uppercase"
            mb={3}
          >
            Logistics & Fulfillment
          </Badge>
          <Heading as="h1" size={{ base: 'xl', md: '2xl' }} fontWeight="extrabold">
            Shipping & Return Policy
          </Heading>
          <Text fontSize="sm" color="gray.300" mt={2}>
            Reliable pan-India delivery for judicial commentaries, bar apparel, and software activations
          </Text>
        </Container>
      </Box>

      {/* Main Document Content */}
      <Container maxW="5xl" px={{ base: 4, md: 8 }}>
        <Box
          bg="white"
          p={{ base: 6, md: 12 }}
          borderRadius="2xl"
          border="1px solid"
          borderColor="gray.100"
          boxShadow="sm"
        >
          {/* Quick Metrics */}
          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={10}>
            <Box p={5} bg="#F8FAFC" borderRadius="xl" border="1px solid" borderColor="gray.100">
              <Icon as={FiClock} boxSize={6} color="#162237" mb={2} />
              <Text fontWeight="bold" fontSize="sm" color="#162237">
                Transit: 2 – 15 Days
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Express air couriers to major metro high courts and surface delivery nationwide.
              </Text>
            </Box>
            <Box p={5} bg="#F8FAFC" borderRadius="xl" border="1px solid" borderColor="gray.100">
              <Icon as={FiTruck} boxSize={6} color="#162237" mb={2} />
              <Text fontWeight="bold" fontSize="sm" color="#162237">
                Complimentary Shipping
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Free pan-India delivery on physical literature orders exceeding ₹1,000.
              </Text>
            </Box>
            <Box p={5} bg="#F8FAFC" borderRadius="xl" border="1px solid" borderColor="gray.100">
              <Icon as={FiRotateCcw} boxSize={6} color="#162237" mb={2} />
              <Text fontWeight="bold" fontSize="sm" color="#162237">
                48-Hour Return Window
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Hassle-free replacement for defective or misprinted law volumes.
              </Text>
            </Box>
          </SimpleGrid>

          <VStack spacing={8} align="stretch" color="gray.700" fontSize="sm" lineHeight="tall">
            <Box>
              <Heading as="h2" size="md" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                1. Pan-India Delivery Schedule & Carriers
              </Heading>
              <Text mb={3}>
                Navneet Law Studios ships across all states and union territories in India. Orders are dispatched from our regional warehouses within 2 to 4 business days of confirmation. Standard courier delivery takes between <strong>2 to 15 business days</strong> depending upon consignee destination and proximity to judicial circuit benches.
              </Text>
              <List spacing={2} pl={2}>
                <ListItem display="flex" alignItems="center">
                  <ListIcon as={FiCheckCircle} color="#059669" />
                  Metro High Court Jurisdictions: 2 – 5 Business Days via Express BlueDart / Delhivery.
                </ListItem>
                <ListItem display="flex" alignItems="center">
                  <ListIcon as={FiCheckCircle} color="#059669" />
                  District & Tehsil Court Towns: 5 – 10 Business Days via Surface Transit.
                </ListItem>
                <ListItem display="flex" alignItems="center">
                  <ListIcon as={FiCheckCircle} color="#059669" />
                  Remote / Island Regions: 7 – 15 Business Days via Indian Speed Post / Registered Mail.
                </ListItem>
              </List>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="md" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                2. Shipping Rates & Transparent Pricing
              </Heading>
              <Text>
                The prices listed on our platform are fully inclusive of statutory packing, secure carton crating, and waterproofing for valuable legal hardbounds. There are zero hidden handling fees. Free shipping automatically applies to physical orders exceeding ₹1,000 (excluding digital SaaS subscriptions and downloadable software licenses, which have instant digital fulfillment).
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="md" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                3. Real-Time Consignment Tracking
              </Heading>
              <Text>
                Every parcel is assigned an individual consignment airway bill (AWB). You can inspect and monitor dispatch progression anytime through the <strong>My Orders</strong> section of your user dashboard or using the live tracking link transmitted via SMS.
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="md" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                4. Returns, Replacements & Non-Returnable Goods
              </Heading>
              <Text mb={3}>
                If a physical volume arrives damaged, misprinted, or with missing pages, please notify our concierge within <strong>48 hours of delivery</strong>. We will arrange a courier pickup and issue a direct replacement at zero surcharge.
              </Text>
              <Box p={4} bg="#F8FAFC" borderRadius="xl" border="1px solid" borderColor="gray.200">
                <Text fontWeight="bold" fontSize="xs" color="#162237" textTransform="uppercase" mb={2}>
                  Non-Returnable Items:
                </Text>
                <List spacing={1.5} fontSize="xs" color="gray.600">
                  <ListItem>• Activated Software Licenses, SaaS Seat Keys & Digital Cloud Subscriptions (Supreme Today AI, CDJ, LegitQuest, DLT, Digilegal).</ListItem>
                  <ListItem>• Downloadable legal templates, PDFs, and electronic journals.</ListItem>
                  <ListItem>• Custom tailored advocate gowns or embroidered bands where individual measurements were supplied.</ListItem>
                  <ListItem>• Bulk institutional orders exceeding 10 copies per title.</ListItem>
                </List>
              </Box>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="md" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                5. Cancellation Protocol
              </Heading>
              <Text>
                Orders may be cancelled free of penalty prior to warehouse dispatch and invoice generation. Once an order is handed to our courier partner, standard return procedures apply. To cancel an active order, email <strong>customercare@navneetlawstudio.com</strong> or call our dispatch desk at <strong>+91 84213 85239</strong> immediately.
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            {/* Helpline Box */}
            <Box bg="#F8FAFC" p={6} borderRadius="xl" border="1px solid" borderColor="gray.200">
              <Heading as="h3" size="sm" color="#162237" mb={2}>
                Dispatch & Tracking Inquiries
              </Heading>
              <Text fontSize="xs" color="gray.500" mb={4}>
                Operating Hours: Monday – Saturday, 9:30 AM – 7:00 PM IST (Excluding Court Holidays)
              </Text>
              <HStack spacing={6} wrap="wrap">
                <HStack spacing={2} fontSize="sm">
                  <Icon as={FiPhone} color="#162237" />
                  <Text><strong>Helpline:</strong> +91 84213 85239 / +91 98111 23456</Text>
                </HStack>
                <HStack spacing={2} fontSize="sm">
                  <Icon as={FiMail} color="#162237" />
                  <Text><strong>Email:</strong> orders@navneetlawstudio.com</Text>
                </HStack>
              </HStack>
            </Box>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}

export default ShippingPolicy;
